import { skipToken, useQuery } from '@tanstack/react-query';
import { t } from 'i18next';

import connection from '~services/connectionModule';

import {
  companyOnboardingRecordFetchers,
  CompanyOnboardingRecordsStepKey,
} from './companyOnboardingRecords';
import { COMPANY_ONBOARDING_STALE_TIME_IN_MS } from './constants';

export const COMPANY_ONBOARDING_RECORDS_QUERY_KEY = 'companyOnboardingRecords';

interface UseCompanyOnboardingRecordsQueryProps {
  companyId: string;
  stepKey: CompanyOnboardingRecordsStepKey;
}

export default function useCompanyOnboardingRecordsQuery({
  companyId,
  stepKey,
}: UseCompanyOnboardingRecordsQueryProps) {
  const fetchRecordsPage = companyOnboardingRecordFetchers[stepKey];
  const { data, isError, isPending, refetch } = useQuery({
    queryKey: [COMPANY_ONBOARDING_RECORDS_QUERY_KEY, companyId, stepKey],
    queryFn: companyId
      ? async ({ signal }) => {
          try {
            return await fetchRecordsPage(companyId, signal);
          } catch (error) {
            connection.handleRequestError(
              error,
              t('error_messages.company_onboarding.failed_to_fetch_records'),
            );
          }
        }
      : skipToken,
    // handleRequestError already toasts, so retries would repeat the same toast.
    retry: false,
    staleTime: COMPANY_ONBOARDING_STALE_TIME_IN_MS,
  });

  return {
    hasMore: data?.hasMore ?? false,
    isError,
    isPending,
    records: data?.records ?? [],
    refetch,
  };
}
